<template>
<v-card width="256" class="fill-height d-flex flex-column align-content-space-between justify-space-between " :class="getRequireQuiz(props.category) ? 'quiz-warning-card' : 'quiz-card'">
  <v-card-title>
    <div class="d-flex justify-space-between fill-width align-center">
      <LTimerPill v-if="!getRequireQuiz(props.category) && lastPositiveAttempt"
                  icon-timer-inactive="mdi-replay"
                  :end-date="DateUtils.getStartOfDate(moment(lastPositiveAttempt.allowReattemptAt), 'day',)"
                  :tooltip-title="quizInfoConfig[category]?.tooltipTitle()"
                  :tooltip-text="quizInfoConfig[category]?.tooltipText()"
                  icon-timer-active="mdi-replay"></LTimerPill>
      <LTimerPill v-else
                  :tooltip-title="quizInfoConfig[category]?.tooltipTitleRequireTest()"
                  :tooltip-text="quizInfoConfig[category]?.tooltipTextRequireTest()"
                  icon-timer-inactive="mdi-lock-outline"
                  color="warning"
                  text-color="anchor"
                  :no-opacity="true"
                  @timerEnded="handleHRlockTimerEnded"
                  :end-date="getRequireHrLockAt(props.category)"
                  icon-timer-active="mdi-lock-open-outline"></LTimerPill>
      <v-tooltip max-width="365px" top>
        <template v-slot:activator="{on}">
          <div v-on="on">
              <div class="circle-box d-flex align-center justify-center" :class="getRequireQuiz(props.category) ? 'primaryBg' : 'secondaryBg'"> <v-icon color="black" small>mdi-alert-circle-outline</v-icon></div>
          </div>
        </template>
        {{quizInfoConfig[category]?.tooltipDescription}}
      </v-tooltip>
    </div>
  </v-card-title>
  <v-card-text class="pb-0">
    <v-spacer></v-spacer>
      <v-img :src="require('@/assets/icons/quizes/statements.svg')" width="68px" height="68px"></v-img>
    <div class="mt-5">
      <span class="block mb-1">{{getRequireQuiz(props.category) ? quizInfoConfig[category]?.quizDuration : quizInfoConfig[category]?.approvedText}}</span>
      <p class="headline-5 mb-1">{{quizInfoConfig[category]?.quizTitle}}</p>
      <p class="body-2 mb-0">
        {{quizInfoConfig[category]?.quizDescription}}
      </p>
    </div>
  </v-card-text>
  <v-card-actions class="fill-width">
    <v-btn
      v-if="quizInfoConfig[category]?.showTestBtn && !getRequireQuiz(props.category)"
      @click="onTakeQuiz"
      outlined
      min-width="100%"
      text
    >
      Zobacz
    </v-btn>
    <v-btn
      v-if="getRequireQuiz(props.category)"
      @click="onTakeQuiz"
      color="primary"
      min-width="100%"
      depressed
      :disabled="hasHrLockTimerEnded"
    >
     {{quizInfoConfig[category]?.startTestBtnText}}
    </v-btn>
  </v-card-actions>
</v-card>
</template>
<script setup lang="ts">
import {QuizTestAttempt} from '@/modules/bhp/models/QuizTest';
import GoToService from '@/router/GoToService';
import LTimerPill from '@/components/commons/LTimerPill.vue';
import DateUtils from '@/services/utils/DateUtils';
import {computed, ref, watchEffect} from 'vue';
import {QuizCategory} from '@/models/Quiz';
import AuthService from '@/modules/user/AuthService';
import moment from 'moment/moment';
import {capitalize} from '@/filters/StringFilters';

const props = defineProps<{
  lastPositiveAttempt?: QuizTestAttempt,
  category: QuizCategory,
}>();
// ten komponent bedzie generyczny w nastepnym tasku, teraz jest cisnienie
const onTakeQuiz = () => {
  GoToService[props.category]();
};

const handleHRlockTimerEnded = () => {
  hasHrLockTimerEnded.value = true;
}

const hasHrLockTimerEnded = ref(false)

const getRequireHrLockAt = (category: QuizCategory) => {
  return AuthService.User?.quiz?.[`require${capitalize(category)}HrLockAt`]!
};

const getRequireQuiz = (category: QuizCategory) => {
  return AuthService.User?.quiz?.[`require${capitalize(category)}`]!
};

type QuizInfoConfig = {
  pathToIcon?: string;
  quizTitle: string;
  quizDescription: string;
  quizDuration: string;
  approvedText: string;
  showTestBtn: boolean;
  tooltipTextRequireTest: () => string;
  tooltipTitleRequireTest: () => string;
  tooltipText: () => string;
  tooltipTitle: () => string;
  tooltipDescription: string;
  startTestBtnText: string;
}
const quizInfoConfig: Partial<Record<QuizCategory,QuizInfoConfig>> = {
  [QuizCategory.STMT]: {
    quizTitle: 'Oświadczenia akredytacyjne',
    quizDuration: 'Zajmie 2 minuty',
    pathToIcon: '@/assets/icons/quizes/statements.svg',
    approvedText: 'Potwierdzone',
    showTestBtn: true,
    startTestBtnText: 'Uzupełnij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin potwierdzenia: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość potwierdzenia będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku potwierdzenia po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Potwierdzenie możliwości dalszego wykonywania zawodu eksperta finansowego',
    tooltipDescription: 'Z uwagi na obowiązki wynikające z przepisów prawa, a także z zawartych umów z bankami, zobowiązany jesteś do złożenia oświadczeń na Platformie Lendi. W przypadku braku potwierdzenia w wyznaczonym terminie, Twoja umowa z Lendi Sp. z o.o. zostanie rozwiązana. Oświadczenia odnawiane są cyklicznie – nie częściej niż raz w roku.',
  },
  [QuizCategory.ETHIC]: {
    quizTitle: 'Szkolenie Etyka i Bezpieczeństwo w sektorze finansowym.',
    pathToIcon: '@/assets/icons/quizes/ethic.svg',
    quizDuration: 'Zajmie 45 minut',
    approvedText: 'Zdane',
    showTestBtn: false,
    startTestBtnText: 'Rozpocznij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin zdania: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość zdania będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku zdania po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Niezbędne do spełniania wymogów Lendi i instytucji finansowych, koncentruje się na kluczowych zasadach etycznych oraz najlepszych praktykach w zakresie ochrony danych i zarządzania ryzykiem',
    tooltipDescription:  'Z uwagi na obowiązki wynikające z przepisów prawa, a także z zawartych umów z bankami, zobowiązany jesteś do realizacji szkoleń udostępnianych na Platformie Lendi. W przypadku braku zdania w wyznaczonym terminie, Twoja umowa z Lendi Sp. z o.o. zostanie rozwiązana. Szkolenia odnawiane są cyklicznie – nie częściej niż raz w roku.',
  },
}

</script>

<style scoped lang="scss">
.quiz-warning-card {
  background-color: colorShade($warning,$border-opacity) !important;
  border-color: var(--v-warning-base) !important;
}
.quiz-card {
  background-color: var(--v-primaryBg-base) !important;
  border-color: fpShadow($border-opacity) !important;
}
.circle-box {
  width: 32px;
  height: 28px;
  border: 1px solid fpShadow($border-opacity) !important;
  border-radius: 24px;
}
</style>
